import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CommonLoader from "../../common/CommonLoader";
import {
  GetSampleMatrices,
  DeleteSampleMatrix,
} from "../../../services/SamplesMaintenanceService";
import DeleteConfirmation from "../../common/DeleteConfirmation"; // Import the DeleteConfirmation component
import { NotificationManager } from "react-notifications";

const SampleMatricesTable = () => {
  const columns = ["Sample Matrix", "Actions"];
  const columnHeaders = ["sampleMatrix"];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [sampleMatricesData, setSampleMatricesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSampleMatrix, setSelectedSampleMatrix] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false); // State for delete confirmation
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null); // State to store the selected item to delete

  useEffect(() => {
    const fetchSampleMatrices = async () => {
      try {
        setLoading(true);
        const response = await GetSampleMatrices();
        setSampleMatricesData(response.results);
        setFilteredData(response.results);
      } catch (error) {
        console.error("Error fetching sample matrices:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSampleMatrices();
  }, []);

  useEffect(() => {
    const filteredResults = sampleMatricesData.filter((sampleMatrix) =>
      columnHeaders.some((header) => {
        const columnValue = sampleMatrix[header];
        return (
          columnValue !== null &&
          columnValue !== undefined &&
          columnValue.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
    );

    setFilteredData(filteredResults);
  }, [searchTerm, sampleMatricesData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (sampleMatrix) => {
    // Handle the action when the "Edit" button is clicked
    // You can navigate to the edit sample matrix page or open a modal, etc.
  };

  const handleDeleteClick = (sampleMatrix) => {
    setSelectedItemToDelete(sampleMatrix);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteSampleMatrix(
        selectedItemToDelete.sampleMatrix
      );
      if (response.results === true) {
        setIsDeleteConfirmationOpen(false);
        const updatedSampleMatrices = sampleMatricesData.filter(
          (item) => item.sampleMatrix !== selectedItemToDelete.sampleMatrix
        );
        setSampleMatricesData(updatedSampleMatrices);
        setFilteredData(updatedSampleMatrices);
        NotificationManager.success("Sample matrix deleted successfully");
      } else {
        setIsDeleteConfirmationOpen(false);
        NotificationManager.error("Error deleting sample matrix");
      }
    } catch (error) {
      NotificationManager.error("Error deleting sample matrix");
      setIsDeleteConfirmationOpen(false);
    }
  };

  return (
    <>
    <span className="title">Test Sample Matrices </span>
    <hr />
      <div
        style={{ display: "flex", marginBottom: "16px", alignItems: "center" }}
      >
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          size="small"
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: "16px", width: "80%" }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={
            () => {} /* Handle the action when the "+ New Sample Matrix" button is clicked */
          }
        >
          New
        </Button>
      </div>
      <Paper style={{ marginRight: "20px" }}>
        <Table sx={{ minWidth: 400 }} size="small">
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((sampleMatrix, index) => (
                <TableRow
                  key={sampleMatrix.sampleMatrix}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                  }}
                >
                  {columnHeaders.map((column) => (
                    <TableCell key={column}>{sampleMatrix[column]}</TableCell>
                  ))}
                  <TableCell>
                    <IconButton
                      aria-label="edit"
                      style={{ color: "orange" }}
                      onClick={() => handleEditClick(sampleMatrix)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      style={{ color: "red" }}
                      onClick={() => handleDeleteClick(sampleMatrix)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <CommonLoader loading={loading} />

      {/* Render the delete confirmation popup */}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete the ${selectedItemToDelete?.sampleMatrix}?`}
        color="red"
      />
    </>
  );
};

export default SampleMatricesTable;
